import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Descriptions,
  Empty,
  message,
  Input,
  DatePicker,
} from 'antd';
import {
  EyeOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { supabase } from '@/shared/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useFormSubmissions } from '../hooks/useFormSubmissions';
import type { FormSubmissionRecord } from '../hooks/useFormSubmissions';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

type FormSubmission = FormSubmissionRecord | any;

interface FormData {
  id: string;
  name: string;
  description: string | null;
  fields: Array<{
    id: string;
    label: string;
    type: string;
    name?: string;
    n8n_name?: string;
  }>;
}

export default function FormSubmissionsPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const queryClient = useQueryClient();

  const loadFormData = async () => {
    try {
      if (!formId) return;
      const { data, error } = await supabase
        .from('forms')
        .select('id, name, description, fields')
        .eq('id', formId)
        .single();
      if (error) throw error;
      setFormData(data as FormData);
    } catch (err) {
      console.error('Error loading form:', err);
      message.error('Erro ao carregar dados do formulário');
    }
  };
  useEffect(() => {
    if (formId) loadFormData();
  }, [formId]);

  // Use server function that returns enriched `userProfile` (SAFER than client side joins)
  const { data: functionSubmissions = [], isLoading: functionIsLoading } = useFormSubmissions({ formId: formId ?? undefined });
  // Keep loading state combined
  useEffect(() => { setLoading(functionIsLoading); }, [functionIsLoading]);
  useEffect(() => {
    setSubmissions(functionSubmissions ?? []);
  }, [functionSubmissions]);

  const handleViewDetails = (record: FormSubmission) => {
    setSelectedSubmission(record);
    setDetailsVisible(true);
  };

  const handleExport = () => {
    try {
      // Create CSV
      const headers = ['Data', 'Cliente', 'Produto', ...Object.keys(submissions[0]?.formData || {})];
      const rows = submissions.map((sub) => [
        dayjs(sub.submission?.submitted_at || sub.submission?.created_at || sub.submitted_at).format('DD/MM/YYYY HH:mm'),
        sub.userProfile?.full_name || '-',
        sub.product?.name || '-',
        ...Object.values(sub.formData ?? sub.submission?.form_data ?? sub.submission?.data ?? {}),
      ]);

      const csv = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${formData?.name || 'formulario'}_${dayjs().format('YYYY-MM-DD')}.csv`;
      link.click();

      message.success('Arquivo exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting:', error);
      message.error('Erro ao exportar dados');
    }
  };

  const filteredSubmissions = submissions.filter((sub) => {
    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesClient = (sub.userProfile?.full_name || '')?.toLowerCase().includes(searchLower);
      const matchesProduct = (sub.product?.name || '')?.toLowerCase().includes(searchLower);
      const matchesData = JSON.stringify(sub.formData ?? sub.submission?.form_data ?? sub.submission?.data ?? {}).toLowerCase().includes(searchLower);
      
      if (!matchesClient && !matchesProduct && !matchesData) {
        return false;
      }
    }

    // Filter by date range
    if (dateRange) {
      const submittedDateStr = sub.submission?.submitted_at || sub.submission?.created_at || sub.submitted_at;
      const submittedDate = dayjs(submittedDateStr);
      if (!submittedDate.isBetween(dateRange[0], dateRange[1], 'day', '[]')) {
        return false;
      }
    }

    return true;
  });

  const columns: ColumnsType<FormSubmission> = [
    {
      title: 'Data de Envio',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      width: 180,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a, b) => dayjs(a.submission?.submitted_at || a.submitted_at).unix() - dayjs(b.submission?.submitted_at || b.submitted_at).unix(),
    },
    {
      title: 'Cliente',
      key: 'client',
      render: (_, record) => (
        <div>
          <div>{record.userProfile?.full_name || '-'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>
            {(record.userProfile?.email || record.user?.email || record.client_product?.client?.email) ?? '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Produto',
      key: 'product',
      render: (_, record) => record.product?.name || '-',
    },
    {
      title: 'Campos Preenchidos',
      key: 'fields_count',
      width: 150,
      render: (_, record) => (
        <Tag color="blue">{Object.keys(record.formData ?? record.submission?.form_data ?? record.submission?.data ?? {}).length} campos</Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          Ver Detalhes
        </Button>
      ),
    },
  ];

  if (!formId) {
    return (
      <Card>
        <Empty description="Formulário não encontrado" />
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/forms')}
            >
              Voltar
            </Button>
            <span>{formData?.name || 'Respostas do Formulário'}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-form-submissions'] })}
            >
              Atualizar
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={submissions.length === 0}
            >
              Exportar CSV
            </Button>
          </Space>
        }
      >
        {formData?.description && (
          <p style={{ marginBottom: 24, color: '#666' }}>{formData.description}</p>
        )}

        <Space style={{ marginBottom: 16, width: '100%' }} size="middle">
          <Input
            placeholder="Buscar por cliente, produto ou dados..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            format="DD/MM/YYYY"
            placeholder={['Data Inicial', 'Data Final']}
          />
          {(searchText || dateRange) && (
            <Tag color="blue">
              {filteredSubmissions.length} de {submissions.length} respostas
            </Tag>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={filteredSubmissions}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} respostas`,
          }}
        />
      </Card>

      {/* Details Modal */}
      <Modal
        title="Detalhes da Resposta"
        open={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setSelectedSubmission(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDetailsVisible(false);
              setSelectedSubmission(null);
            }}
          >
            Fechar
          </Button>,
        ]}
        width={700}
      >
        {selectedSubmission && (
          <div>
            <Descriptions column={1} bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Cliente">
                {selectedSubmission.userProfile?.full_name || selectedSubmission.clientProduct?.client?.user_profile?.full_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="E-mail">
                {selectedSubmission.clientProduct?.client?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Produto">
                {selectedSubmission.product?.name || selectedSubmission.clientProduct?.product?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Data de Envio">
                {dayjs(selectedSubmission.submitted_at).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            <h3>Dados do Formulário</h3>
            <Descriptions column={1} bordered>
                {formData?.fields.map((field) => {
                const value = (selectedSubmission.formData ?? selectedSubmission.submission?.form_data ?? selectedSubmission.submission?.data ?? {})[field.id] ?? (field.name ? (selectedSubmission.formData ?? selectedSubmission.submission?.form_data ?? {})[field.name] : undefined) ?? (field.n8n_name ? (selectedSubmission.formData ?? selectedSubmission.submission?.form_data ?? {})[field.n8n_name] : undefined);
                const label = field.label || field.n8n_name || field.name || field.id;
                return (
                  <Descriptions.Item key={field.id} label={label}>
                    {Array.isArray(value) ? value.join(', ') : value ?? '-'}
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
}
